import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Globe, MapPin } from 'lucide-react';
import { SortOption, SortDirection } from '../hooks/useXMRBusinesses';

interface SortControlsProps {
  sortBy: SortOption;
  sortDirection: SortDirection;
  onSortChange: (sortBy: SortOption, direction: SortDirection) => void;
  globalSearch: boolean;
  onGlobalSearchToggle: (global: boolean) => void;
  businessCount: number;
}

const sortOptions: { value: SortOption; label: string; icon: string }[] = [
  { value: 'name', label: 'Name', icon: '🔤' },
  { value: 'category', label: 'Category', icon: '📂' },
  { value: 'country', label: 'Country', icon: '🌍' },
  { value: 'rating', label: 'Rating', icon: '⭐' },
  { value: 'reviews', label: 'Reviews', icon: '💬' },
  { value: 'distance', label: 'Distance', icon: '📍' },
];

export const SortControls: React.FC<SortControlsProps> = ({
  sortBy,
  sortDirection,
  onSortChange,
  globalSearch,
  onGlobalSearchToggle,
  businessCount
}) => {
  const handleSortChange = (newSortBy: SortOption) => {
    if (newSortBy === sortBy) {
      // Toggle direction if same sort option
      onSortChange(sortBy, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default direction for new sort option
      const defaultDirection = ['rating', 'reviews'].includes(newSortBy) ? 'desc' : 'asc';
      onSortChange(newSortBy, defaultDirection);
    }
  };

  const getSortIcon = (option: SortOption) => {
    if (option !== sortBy) {
      return <ArrowUpDown className="w-4 h-4 text-gray-500" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-orange-400" /> : 
      <ArrowDown className="w-4 h-4 text-orange-400" />;
  };

  return (
    <div className="space-y-4">
      {/* Global/Regional Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {globalSearch ? (
              <Globe className="w-5 h-5 text-orange-400" />
            ) : (
              <MapPin className="w-5 h-5 text-blue-400" />
            )}
            <span className="text-white font-medium">
              {globalSearch ? 'Global Search' : 'Regional Search'}
            </span>
          </div>
          <div className="text-sm text-gray-400">
            ({businessCount} businesses)
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onGlobalSearchToggle(false)}
            className={`px-3 py-1 rounded-lg text-sm transition-all ${
              !globalSearch
                ? 'bg-blue-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Regional
          </button>
          <button
            onClick={() => onGlobalSearchToggle(true)}
            className={`px-3 py-1 rounded-lg text-sm transition-all ${
              globalSearch
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Global
          </button>
        </div>
      </div>

      {/* Sort Options */}
      <div className="p-4 bg-gray-900/50 rounded-xl border border-gray-700">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4" />
          Sort Businesses
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSortChange(option.value)}
              className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                sortBy === option.value
                  ? 'bg-orange-500/20 border-orange-500 text-orange-300'
                  : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:bg-gray-700/50 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{option.icon}</span>
                <span className="font-medium text-sm">{option.label}</span>
              </div>
              {getSortIcon(option.value)}
            </button>
          ))}
        </div>
        
        {/* Current Sort Display */}
        <div className="mt-3 pt-3 border-t border-gray-700">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>Sorted by:</span>
            <span className="text-orange-400 font-medium">
              {sortOptions.find(opt => opt.value === sortBy)?.label}
            </span>
            <span className="text-gray-500">
              ({sortDirection === 'asc' ? 'A-Z' : 'Z-A'})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};